// src/lib/patientIntroPreview.js

import { normalizeConditionList } from "@/utils/conditions";
import { isOwnedDraftForUser } from "@/lib/ownedDrafts.mjs";

const KEY = "afrimed_patient_intro_preview_v1";

function scopedKey(userId = "") {
  const uid = String(userId || "").trim();
  return uid ? `${KEY}:${uid}` : KEY;
}

const EMPTY = {
  name: "",
  full_name: "",
  dobIso: "",

  // Contact
  phone: "",
  phoneVerified: false,
  email: "",
  emailVerified: false,
  nationalId: "",
  nationalIdVerified: false,

  // Address
  addressLine: "",
  addressLine2: "",
  addressStreet: "",
  city: "",
  postalCode: "",
  addressPostalCode: "",
  country: "",
  addressCountry: "",

  // Languages (simple array of {id,label} or strings)
  languages: [],

  // Medical info
  bloodType: "",
  allergies: "",
  conditions: "",
  heightCm: "",
  weightKg: "",
  organDonor: null, // true / false / null
};

function toText(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.toLowerCase() === "[object object]" ? "" : trimmed;
  }
  if (typeof value === "number") return String(value);
  return "";
}

function textFromUnknown(value) {
  if (Array.isArray(value)) return value.map(textFromUnknown).filter(Boolean).join(", ");
  if (value && typeof value === "object") {
    return (
      toText(value.label) ||
      toText(value.language) ||
      toText(value.name) ||
      toText(value.value) ||
      toText(value.text) ||
      toText(value.title) ||
      toText(value.country) ||
      toText(value.code) ||
      ""
    );
  }
  return toText(value);
}

function listFromUnknown(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => textFromUnknown(entry)).filter(Boolean);
  }
  if (value && typeof value === "object") {
    const one = textFromUnknown(value);
    return one ? [one] : [];
  }
  const text = toText(value);
  if (!text) return [];
  return text
    .split(",")
    .map((part) => toText(part))
    .filter(Boolean);
}

function normalizePreview(parsed) {
  const src = parsed && typeof parsed === "object" ? parsed : {};

  const addressLine = textFromUnknown(src.addressLine || src.addressStreet || "");
  const addressLine2 = textFromUnknown(src.addressLine2 || src.city || "");
  const postalCode = textFromUnknown(src.postalCode || src.addressPostalCode || "");
  const country = textFromUnknown(src.country || src.addressCountry || "");
  const name = textFromUnknown(src.name || src.full_name || "");

  return {
    ...EMPTY,
    ...src,
    name,
    full_name: textFromUnknown(src.full_name || name),
    addressLine,
    addressStreet: textFromUnknown(src.addressStreet || addressLine),
    addressLine2,
    city: textFromUnknown(src.city || addressLine2),
    postalCode,
    addressPostalCode: textFromUnknown(src.addressPostalCode || postalCode),
    country,
    addressCountry: textFromUnknown(src.addressCountry || country),
    languages: listFromUnknown(src.languages),
    bloodType: textFromUnknown(src.bloodType),
    allergies: listFromUnknown(src.allergies).join(", "),
    conditions: normalizeConditionList(listFromUnknown(src.conditions)).join(", "),
    heightCm: textFromUnknown(src.heightCm || src.height_cm),
    weightKg: textFromUnknown(src.weightKg || src.weight_kg),
    phone: textFromUnknown(src.phone),
    email: textFromUnknown(src.email),
    nationalId: textFromUnknown(src.nationalId),
    dobIso: textFromUnknown(src.dobIso),
    organDonor:
      typeof src.organDonor === "boolean"
        ? src.organDonor
        : textFromUnknown(src.organDonor),
  };
}

function readPreviewPayload(key) {
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  const parsed = JSON.parse(raw);
  return parsed && typeof parsed === "object" ? parsed : null;
}

export function loadPatientIntroPreview(userId = "") {
  if (typeof window === "undefined") return EMPTY;
  const uid = String(userId || "").trim();
  try {
    let parsed = uid ? readPreviewPayload(scopedKey(uid)) : readPreviewPayload(KEY);
    if (!parsed && uid) {
      parsed = readPreviewPayload(KEY);
      if (!isOwnedDraftForUser(parsed, uid)) return EMPTY;
    }
    if (!parsed) return EMPTY;
    return normalizePreview(parsed);
  } catch {
    return EMPTY;
  }
}

export function savePatientIntroPreview(patch, userId = "") {
  if (typeof window === "undefined") return EMPTY;
  const uid = String(userId || "").trim();
  const current = loadPatientIntroPreview(uid);
  const next = normalizePreview({
    ...current,
    ...patch,
    ...(uid ? { __ownerUserId: uid } : {}),
  });
  window.localStorage.setItem(scopedKey(uid), JSON.stringify(next));
  return next;
}

export function clearPatientIntroPreview(userId = "") {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(scopedKey(userId));
    if (!String(userId || "").trim()) window.localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
